import { PillButton } from "@/components/Button/PillButton";
import { InsufficientCreditsDialog } from "@/components/DialogBox/InsufficientCreditDialog";
import { SubmitForDesignSvg } from "@/components/Icon/Icon";
import { CASE_TABLE_STATE_NAME } from "@/constants/designTableConstants";
import { InsufficientCreditsAPIError, IsAPIError } from "@/constants/errors";
import { Service, useOrgCredits, useServiceCost } from "@/hooks/credits";
import { CaseReadPublic, useMarkCaseUploadedApiV1CasesCaseIdUploadedPutMutation } from "@/store/baseClientApi";
import { clientApi } from "@/store/clientApi";
import { selectCheckedIds, selectRtkData } from "@/store/slices/tableSlice";
import { RootState } from "@/store/store";
import { ensureError } from "@/utils/helpers";
import { createSelector } from "@reduxjs/toolkit";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { shallowEqual, useSelector } from "react-redux";

const selectUnpaidUploads = createSelector(
  [
    (state: RootState) => selectRtkData(state, CASE_TABLE_STATE_NAME, clientApi.endpoints.getCasesApiV1CasesGet.select)?.data?.content,
    (state: RootState) => selectCheckedIds(state, CASE_TABLE_STATE_NAME),
    (state: RootState, selectedCase: string[] | undefined) => selectedCase
  ],
  (content, checkedIds, selectedCase) => {
    if (selectedCase !== undefined) {
      checkedIds = selectedCase;
    }

    const dentalCases = checkedIds
      ? checkedIds
          .map((id) => content?.find((dentalCase) => dentalCase.id.toString() === id))
          .filter((dentalCase): dentalCase is CaseReadPublic => dentalCase !== undefined && dentalCase.id > 0)
      : [];

    return dentalCases.filter((dentalCase) => dentalCase.status === "unpaid_upload").map((dentalCase) => dentalCase.id.toString());
  },
  {
    memoizeOptions: {
      resultEqualityCheck: shallowEqual
    }
  }
);

export const ProcessUnpaidUploadButton = ({ selectedCases }: { selectedCases?: string[] }) => {
  const { t } = useTranslation("cases");
  const [markCaseAsUploaded] = useMarkCaseUploadedApiV1CasesCaseIdUploadedPutMutation();
  const unpaidUploadIds = useSelector((state: RootState) => selectUnpaidUploads(state, selectedCases));
  const [insufficientCreditsDialogOpen, setInsufficientCreditsDialogOpen] = useState<boolean>(false);
  const { numCredits, isLoading, error } = useOrgCredits();
  const { totalCost } = useServiceCost({
    products: {
      [Service.ModelBasing]: unpaidUploadIds.length
    }
  });
  const isOutOfCredits = (numCredits ?? 0) < totalCost;
  const hasCredits = (numCredits ?? 0) !== 0;

  const processCases = async () => {
    let failedCount = 0;
    let insufficientFunds = false;

    const loadingId = toast.loading(t("message.processingCases", { count: unpaidUploadIds.length }));

    await Promise.allSettled(
      unpaidUploadIds.map(async (id) => {
        try {
          await markCaseAsUploaded({ caseId: Number.parseInt(id) }).unwrap();
        } catch (err) {
          if (IsAPIError(InsufficientCreditsAPIError, ensureError(err))) {
            insufficientFunds = true;
          }
          failedCount++;
        }
      })
    );

    if (insufficientFunds) {
      setInsufficientCreditsDialogOpen(true);
    }
    if (failedCount > 0) {
      toast.error(t("error.failedToProcessCases", { count: failedCount }));
    }

    toast.dismiss(loadingId);
  };

  if (unpaidUploadIds.length === 0 || !hasCredits || isLoading || error) {
    return <></>;
  }

  return (
    <>
      <PillButton disabled={isOutOfCredits} onClick={() => processCases()} startIcon={<SubmitForDesignSvg color="inherit" />}>
        {t("common:button.process")}
      </PillButton>
      <InsufficientCreditsDialog open={insufficientCreditsDialogOpen} setOpen={setInsufficientCreditsDialogOpen} showExtendedMessage />
    </>
  );
};
